import React, { useState, useMemo, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { I, COLUMNS, UNI_COLORS } from "../data";
import { useWorkspace } from "../WorkspaceContext";
import { useAuth } from "../AuthContext";
import { useUpgrade, FEATURES, readPlanError } from "../UpgradeContext";
import { useCopilot } from "../CopilotContext";
import UpgradeButton from "../components/UpgradeButton";

// ─────────── Board data-integrity helpers (mirror backend validation) ───────────
// Used for instant add-time feedback + subtle warning badges on cards.
const FIELD_FAMILIES = [
  ["Business", /\b(mba|business|management|finance|marketing|accounting|entrepreneur|supply chain|\bhr\b|hospitality|commerce)\b/i],
  ["Computing", /\b(computer science|comp\.? sci|\bcs\b|software|data science|data analytics|artificial intelligence|machine learning|\bai\b|\bml\b|information technology|computing|informatics|cyber\s?security|computer engineering|\bhci\b)\b/i],
  ["Law", /\b(law|ll\.?m|ll\.?b|juris|legal studies)\b/i],
  ["Medicine & Health", /\b(medicine|medical|\bmd\b|nursing|pharmacy|public health|dentistry|dental|veterinary|physiotherapy)\b/i],
  ["Engineering", /\b(mechanical|electrical|civil|aerospace|chemical engineering|biomedical engineering|industrial engineering|materials science|robotics|mechatronics)\b/i],
  ["Arts & Humanities", /\b(liberal arts|history|philosophy|literature|linguistics|fine arts|graphic design|architecture|music|film|media studies|journalism|communications?)\b/i],
  ["Social Science", /\b(psychology|sociology|anthropology|political science|international relations|public policy|social work|economics)\b/i],
  ["Natural Science", /\b(physics|chemistry|biology|mathematics|\bmaths?\b|statistics|neuroscience|environmental science|geology|astronomy|biochemistry)\b/i],
  ["Education", /\b(education|teaching|pedagogy|curriculum)\b/i],
];
function fieldFamily(text) {
  const s = text || "";
  if (!s.trim()) return null;
  for (const [label, rx] of FIELD_FAMILIES) if (rx.test(s)) return label;
  return null;
}
function programLevel(text) {
  const s = text || "";
  if (!s.trim()) return null;
  if (/\b(ph\.?\s?d|d\.?phil|doctora(l|te)|ed\.?d|d\.?sc)\b/i.test(s)) return "doctoral";
  if (/\b(m\.?s\.?c?|m\.?a|m\.?b\.?a|m\.?eng|m\.?fa|ll\.?m|m\.?p\.?h|m\.?p\.?a|m\.?s\.?w|mres|master'?s?|post\s?grad(uate)?|graduate)\b/i.test(s)) return "graduate";
  if (/\b(b\.?s\.?c?|b\.?a|b\.?eng|b\.?tech|bachelor'?s?|under\s?grad(uate)?|associate'?s?|diploma|foundation|hnd)\b/i.test(s)) return "undergraduate";
  return null;
}
function profileLevel(intended) {
  const s = (intended || "").toLowerCase();
  if (!s.trim()) return null;
  if (/phd|doctor|dphil/.test(s)) return "doctoral";
  // Check undergraduate FIRST — "undergraduate" contains "grad", which would
  // otherwise be misread as a graduate-level goal.
  if (/under\s?grad|undergraduate|bachelor|\bug\b|high\s?school|freshman|sophomore|junior|senior/.test(s)) return "undergraduate";
  if (/master|postgrad|mba|\bgraduate\b|\bgrad\b|m\.?s/.test(s)) return "graduate";
  return null;
}
// Returns {kind, short, reason} when a program conflicts with the profile, else null.
function evaluateFit(program, profile) {
  const prog = (program || "").trim();
  if (!prog) return null;
  const pb = profileLevel(profile?.intended_level);
  const gb = programLevel(prog);
  if (pb && gb && pb !== gb) {
    return { kind: "level", short: `${gb} vs your ${pb} goal`,
      reason: `This looks like an ${gb}-level program, but your profile is ${pb}-level.` };
  }
  const pf = fieldFamily(profile?.intended_major);
  const gf = fieldFamily(prog);
  if (pf && gf && pf !== gf) {
    return { kind: "field", short: `${gf} vs your ${pf}`,
      reason: `This program looks like ${gf}, but your intended major is ${profile?.intended_major} (${pf}).` };
  }
  return null;
}
// Deadline state: { passed, label } — parses "Jan 5", ISO, "Dec 1, 2025", etc.
function deadlineState(deadline) {
  const raw = (deadline || "").trim();
  if (!raw) return { passed: false, missing: true };
  if (/rolling|no deadline|tba|ongoing|open/i.test(raw)) return { passed: false };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const hasYear = /\b\d{4}\b/.test(raw);
  let d = null;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) d = new Date(+iso[1], +iso[2] - 1, +iso[3]);
  else {
    let tmp = new Date(hasYear ? raw : `${raw} ${today.getFullYear()}`);
    if (!isNaN(tmp.getTime())) d = tmp;
  }
  if (!d || isNaN(d.getTime())) return { passed: false, unparsed: true };
  d.setHours(0, 0, 0, 0);
  // Bare month-day (no year): a deadline landing far in the future is almost
  // always a prior-cycle application deadline, so treat it as last year's.
  if (!hasYear) {
    const daysAhead = (d - today) / 86400000;
    if (daysAhead > 120) d.setFullYear(d.getFullYear() - 1);
  }
  return { passed: d < today };
}
// Warning badges for a card given the user's profile.
function cardWarnings(u, profile) {
  const w = [];
  const fit = evaluateFit(u.program, profile);
  if (fit) w.push({ kind: "off-profile", label: "Off-profile", tip: fit.reason });
  const ds = deadlineState(u.deadline);
  if (ds.passed && (u.status || "") !== "offer") w.push({ kind: "deadline", label: "Deadline passed", tip: `The deadline (${u.deadline}) has already passed.` });
  if (ds.missing && ["applied", "offer"].includes(u.status || "")) w.push({ kind: "nodata", label: "No deadline", tip: "This application has no deadline on record — add or verify it." });
  return w;
}

export default function Board({ openDetail }) {
  const { universities, updateUniversity, deleteUniversity, addUniversity, profile } = useWorkspace();
  const { openCopilot } = useCopilot();
  const { user } = useAuth();
  const { open: openUpgrade } = useUpgrade();
  const [dragId, setDragId] = useState(null);
  const dragIdRef = useRef(null);
  const [overCol, setOverCol] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showCapModal, setShowCapModal] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const [progFilter, setProgFilter] = useState("all"); // all | notstarted | inprogress | ready

  const matchesProg = (u) => {
    if (progFilter === "all") return true;
    const p = u.progress || 0;
    if (progFilter === "notstarted") return p <= 0;
    if (progFilter === "inprogress") return p > 0 && p < 100;
    if (progFilter === "ready") return p >= 100;
    return true;
  };

  const isNavigator = user?.plan_summary?.is_navigator || user?.plan === "navigator";
  const uniLimit = isNavigator ? null : 1;
  const atCap = !isNavigator && universities.length >= 1;

  const onClickAdd = () => {
    if (atCap) {
      setShowCapModal(true);
      return;
    }
    setShowAdd(true);
  };

  const onDragStart = (e, id) => { dragIdRef.current = id; setDragId(id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(id)); } catch (_) {} };
  const onDragEnd = () => { dragIdRef.current = null; setDragId(null); setOverCol(null); };
  const onDragOver = (e, colId) => { e.preventDefault(); setOverCol(colId); };
  const onDrop = async (e, colId) => {
    e.preventDefault();
    let id = dragIdRef.current;
    try { id = e.dataTransfer.getData("text/plain") || id; } catch (_) {}
    dragIdRef.current = null; setDragId(null); setOverCol(null);
    if (id) await updateUniversity(id, { status: colId });
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 4 ? prev : [...prev, id]));
  };

  const openCompare = () => {
    if (!compareOpen && compareIds.length === 0) {
      // Seed with the first 2-3 universities the user is tracking, so the drawer
      // isn't empty on first open.
      setCompareIds(universities.slice(0, Math.min(3, universities.length)).map((u) => u.id));
    }
    setCompareOpen((o) => !o);
  };

  const DOTS = ["var(--indigo-300)", "var(--indigo)", "var(--indigo-deep)", "var(--ok)"];

  return (
    <div className="vwrap vboard-view" data-testid="board-view">
      <div className="vphead">
        <div>
          <div className="ptitle">My Universities</div>
          <div className="psub">
            Your whole journey, researching to offer — {universities.length} school{universities.length === 1 ? "" : "s"}. Tap any card to open its page.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {universities.length >= 2 && (
            <button className="addu" style={{ padding: "11px 16px", borderStyle: "solid" }} onClick={openCompare} data-testid="board-compare-btn">
              <I.Sparkle size={14} /> Compare {compareIds.length > 0 ? `(${compareIds.length})` : ""}
            </button>
          )}
          <button className="vp-cta" onClick={() => openCopilot("suggestUnis")} data-testid="board-add-uni-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1"><path d="M12 3v18M3 12h18" /></svg>
            Find universities with Vidhya
          </button>
        </div>
      </div>

      {!isNavigator && (
        <div className="explorer-board-banner" data-testid="explorer-board-banner">
          <div>
            <strong>Explorer plan · 1 university tracked</strong>
            <p>Upgrade to Navigator to add every school you're applying to — no caps.</p>
          </div>
          <UpgradeButton feature={FEATURES.UNI_BOARD} context="board_banner" label="Unlock unlimited" size="sm" testid="board-banner-upgrade-btn" />
        </div>
      )}

      <div className="filt board-progfilt" style={{ marginBottom: 16 }} data-testid="board-progress-filter">
        {[
          { id: "all", label: "All" },
          { id: "notstarted", label: "Not started" },
          { id: "inprogress", label: "In progress" },
          { id: "ready", label: "Ready" },
        ].map((f) => {
          const count = f.id === "all"
            ? universities.length
            : universities.filter((u) => {
                const p = u.progress || 0;
                if (f.id === "notstarted") return p <= 0;
                if (f.id === "inprogress") return p > 0 && p < 100;
                return p >= 100;
              }).length;
          return (
            <button
              key={f.id}
              className={`fbtn ${progFilter === f.id ? "on" : ""}`}
              onClick={() => setProgFilter(f.id)}
              data-testid={`board-progfilter-${f.id}`}
            >
              {f.label} <span style={{ opacity: 0.6, fontWeight: 700 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {compareOpen && universities.length >= 2 && (
        <CompareSheet universities={universities} compareIds={compareIds} onToggle={toggleCompare} onClose={() => setCompareOpen(false)} />
      )}

      <div className="board">
        {COLUMNS.map((col, ci) => {
          const items = universities.filter((u) => u.status === col.id && matchesProg(u));
          return (
            <div
              className={`bcol ${overCol === col.id ? "dragover" : ""}`}
              key={col.id}
              data-testid={`column-${col.id}`}
              onDragOver={(e) => onDragOver(e, col.id)}
              onDrop={(e) => onDrop(e, col.id)}
              onDragLeave={() => setOverCol((c) => (c === col.id ? null : c))}
            >
              <div className="bch">
                <b><span className="bdot" style={{ background: DOTS[ci] }} />{col.name}</b>
                <span className="ct">{items.length}</span>
              </div>
              {items.map((u) => (
                <UniCard
                  key={u.id}
                  u={u}
                  warnings={cardWarnings(u, profile)}
                  dragging={dragId === u.id}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onClick={() => openDetail(u.id)}
                />
              ))}
              {col.id === "researching" && (
                <div className="addu" onClick={onClickAdd} data-testid="board-col-add">+ Add a university</div>
              )}
              {items.length === 0 && col.id !== "researching" && (
                <div className="bempty">Drop a university here</div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && (
        <AddUniversityModal
          onClose={() => setShowAdd(false)}
          addUniversity={addUniversity}
          openDetail={openDetail}
          onPlanLimit={(err) => {
            setShowAdd(false);
            const pe = readPlanError(err);
            if (pe) {
              setShowCapModal(true);
            }
          }}
        />
      )}
      {showCapModal && (
        <UniCapModal
          existing={universities[0]}
          onClose={() => setShowCapModal(false)}
          onUpgrade={() => {
            setShowCapModal(false);
            openUpgrade(FEATURES.UNI_BOARD, "board_cap_modal");
          }}
          onReplace={async () => {
            if (!universities[0]) return;
            await deleteUniversity(universities[0].id);
            setShowCapModal(false);
            setShowAdd(true);
          }}
        />
      )}
    </div>
  );
}

function UniCapModal({ existing, onClose, onUpgrade, onReplace }) {
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="uni-cap-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <div className="eyebrow">Explorer plan</div>
        <h3 style={{ marginTop: 6 }}>You're at your 1-university cap.</h3>
        <p style={{ color: "var(--ink-3)", fontSize: 14, lineHeight: 1.55, marginTop: 6 }}>
          Explorer tracks one university so you can try Vidhya end-to-end. Navigator tracks
          <strong> every</strong> school you're applying to — no caps, no compromises.
        </p>
        {existing && (
          <div className="cap-modal-existing" data-testid="cap-modal-existing">
            <span className="rail-uni-mark" style={{ background: existing.color, fontFamily: "var(--font-display)" }}>
              {(existing.short || existing.name).slice(0, 2)}
            </span>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{existing.name}</div>
              <div className="muted" style={{ fontSize: 11 }}>Currently tracked</div>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
          <button
            className="btn btn-ghost"
            onClick={onReplace}
            data-testid="cap-modal-replace-btn"
          >
            Replace existing
          </button>
          <button
            className="btn btn-clay"
            onClick={onUpgrade}
            style={{ flex: 1, minWidth: 180 }}
            data-testid="cap-modal-upgrade-btn"
          >
            Upgrade to Navigator
          </button>
        </div>
      </div>
    </div>
  );
}

function UniCard({ u, warnings = [], dragging, onDragStart, onDragEnd, onClick }) {
  const pct = u.progress || 0;
  const filled = pct >= 100;
  return (
    <div
      className={`ucard ${dragging ? "dragging" : ""}`}
      draggable
      onDragStart={(e) => onDragStart(e, u.id)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`uni-card-${u.id}`}
    >
      <div className="uct">
        <div className="ulogo">{(u.short || u.name)[0]}</div>
        <div style={{ minWidth: 0 }}>
          <b>{u.name}</b><br />
          <span>{u.program || u.loc || "—"}</span>
        </div>
      </div>
      <div className="umeta">
        <div className="upg"><i style={{ width: `${pct}%`, ...(filled ? { background: "var(--ok)" } : {}) }} /></div>
        <span className={`udue ${u.deadlineUrgent ? "soon" : ""}`}>{u.status === "applied" ? "Submitted" : u.status === "offer" ? "Offer" : (u.deadline || "—")}</span>
      </div>
      {warnings.length > 0 && (
        <div className="uwarn-row" data-testid={`uni-warnings-${u.id}`}>
          {warnings.map((w) => (
            <span key={w.kind} className="uwarn-chip" title={w.tip} data-testid={`uni-warn-${w.kind}-${u.id}`}>
              <AlertTriangle size={11} /> {w.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function AddUniversityModal({ onClose, addUniversity, onPlanLimit, openDetail }) {
  const { profile } = useWorkspace();
  const [name, setName] = useState("");
  const [program, setProgram] = useState(() => (profile?.intended_major || "").trim());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Disambiguation state. We ALWAYS confirm the name against the AI
  // disambiguator before creating a university — this prevents a random word
  // or a misspelling from silently becoming a real (wrong) school.
  //   candidates = []   → none fetched yet
  //   noMatch = true    → the input is not a real university; refuse + ask to clarify
  //   confirmedName     → the canonical name the user (or a single high-confidence hit) locked in
  const [candidates, setCandidates] = useState([]);
  const [disambig, setDisambig] = useState(false);
  const [confirmedName, setConfirmedName] = useState("");
  const [noMatch, setNoMatch] = useState(false);

  const resetDisambig = () => {
    setCandidates([]);
    setConfirmedName("");
    setNoMatch(false);
    setError("");
  };

  const requestCandidates = async () => {
    setDisambig(true);
    setError("");
    setNoMatch(false);
    try {
      const { http } = await import("../api");
      const { data } = await http.post("/ai/disambiguate-university", {
        query: name,
        program,
        student_country: profile?.student_country || profile?.citizenship || "",
      });
      const cands = data?.candidates || [];
      if (data?.no_match || cands.length === 0) {
        // Not a real university — refuse to create and ask the user to clarify.
        setNoMatch(true);
        setCandidates([]);
        return;
      }
      // A single, high-confidence exact match → adopt it directly.
      if (cands.length === 1 && cands[0].confidence === "high") {
        setConfirmedName(cands[0].name);
        setCandidates([]);
        return;
      }
      // Otherwise the user must explicitly pick (ambiguous or low-confidence).
      setCandidates(cands);
    } catch (err) {
      console.warn("[board] disambiguation failed:", err);
      // On a backend error we still don't silently create — let the user retry.
      setError("Couldn't verify that name just now. Try again.");
    } finally {
      setDisambig(false);
    }
  };

  const pickCandidate = (c) => {
    setConfirmedName(c.name);
    setCandidates([]);
    setNoMatch(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const raw = name.trim();
    if (!raw) return;

    // Always confirm the name first. Only after we have a confirmedName do we create.
    if (!confirmedName) {
      if (noMatch) return;                       // input isn't a real university
      if (candidates.length > 0) return;         // waiting for the user to pick
      return requestCandidates();                // kick off verification
    }

    const finalName = confirmedName;

    // Client-side profile-fit pre-check — instant feedback before we spend an
    // enrich call. The server enforces this too (source of truth).
    const fit = evaluateFit(program, profile);
    if (fit) {
      setError(`${fit.reason} Vidhya keeps your board aligned with your goal. If your plans changed, update your profile first.`);
      return;
    }

    setBusy(true);
    setError("");
    try {
      const color = UNI_COLORS[Math.floor(Math.random() * UNI_COLORS.length)];
      // Create immediately with status 'researching'. The backend kicks off live
      // auto-research in the background (populates tuition / deadline / fit /
      // checklist), so we no longer block the modal on a synchronous enrich call.
      // We route the user straight into the Detail page so they SEE the transparent
      // step-by-step research reveal while it runs.
      const created = await addUniversity({ name: finalName, program, color, status: "researching" });
      onClose();
      if (created?.id && openDetail) openDetail(created.id);
    } catch (err) {
      const pe = readPlanError(err);
      if (pe && onPlanLimit) {
        onPlanLimit(err);
        return;
      }
      setError(err?.response?.data?.detail || err?.message || "Could not add — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="add-uni-modal">
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>Add a university</h3>
        <form onSubmit={submit}>
          <div className="auth-field">
            <label>University name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => { setName(e.target.value); resetDisambig(); }}
              placeholder="Carnegie Mellon University"
              required
              autoFocus
              data-testid="add-uni-name"
            />
            {confirmedName && (
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6 }} data-testid="add-uni-confirmed">
                Will be added as: <strong style={{ color: "var(--ink)" }}>{confirmedName}</strong>{" "}
                <button type="button" className="btn btn-sm btn-ghost" style={{ padding: "1px 8px" }} onClick={resetDisambig} data-testid="add-uni-change">change</button>
              </div>
            )}
          </div>
          <div className="auth-field">
            <label>Program</label>
            <input className="input" value={program} onChange={(e) => setProgram(e.target.value)} placeholder={profile?.intended_major ? `${profile.intended_major}` : "MS in Computer Science"} data-testid="add-uni-program" />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)", marginTop: 8 }}>
            <I.Sparkle size={12} /> Vidhya will research this live the moment you add it.
          </div>

          {noMatch && (
            <div style={{ marginTop: 14, background: "var(--clay-wash)", border: "1px solid var(--clay-soft)", borderRadius: 12, padding: 12 }} data-testid="add-uni-no-match">
              <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 500 }}>
                Hmm, "{name.trim()}" doesn't match a university I can verify.
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
                Check the spelling or type the full official name (e.g. "University of San Francisco"), then verify again.
              </div>
            </div>
          )}

          {candidates.length > 0 && (
            <div style={{ marginTop: 14, background: "var(--bg-sunk)", border: "1px solid var(--ink-5)", borderRadius: 12, padding: 12 }} data-testid="add-uni-candidates">
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-4)", marginBottom: 8 }}>
                "{name.trim()}" matches several universities — pick one
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {candidates.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => pickCandidate(c)}
                    data-testid={`add-uni-candidate-${i}`}
                    style={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: c.is_top_match ? "1px solid var(--clay-soft)" : "1px solid transparent",
                      background: c.is_top_match ? "var(--clay-wash)" : "transparent",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                        <strong style={{ fontSize: 13, color: "var(--ink)" }}>{c.name}</strong>
                        {c.is_top_match && (
                          <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--clay-deep)", letterSpacing: "0.08em", textTransform: "uppercase" }}>most likely</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>
                        {[c.city, c.country].filter(Boolean).join(", ")}{c.why ? ` — ${c.why}` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <div className="auth-error" style={{ marginTop: 10 }} data-testid="add-uni-error">{typeof error === "string" ? error : "Could not add — try again."}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 18, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn-sm btn-clay"
              disabled={busy || disambig || noMatch || (candidates.length > 0 && !confirmedName)}
              data-testid="add-uni-submit"
            >
              {busy ? "Adding…" : disambig ? "Verifying…" : (confirmedName ? "Add" : "Verify name")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function CompareSheet({ universities, compareIds, onToggle, onClose }) {
  const selected = useMemo(
    () => compareIds.map((id) => universities.find((u) => u.id === id)).filter(Boolean),
    [universities, compareIds]
  );

  // Rows are objects that know how to render each uni; null cells show as "—".
  const rows = [
    { label: "Program", render: (u) => u.program || "—" },
    { label: "Location", render: (u) => u.loc || "—" },
    { label: "Tier", render: (u) => u.tier ? u.tier : "—" },
    { label: "Deadline", render: (u) => u.deadline || "—" },
    { label: "Tuition", render: (u) => u.tuition || "—" },
    { label: "Acceptance", render: (u) => u.acceptRate || "—" },
    { label: "Rank", render: (u) => u.rank || "—" },
    { label: "Intl %", render: (u) => u.intlPct || "—" },
    { label: "GPA req.", render: (u) => u.gpa_required || "—" },
    { label: "English min.", render: (u) => u.english_min || "—" },
    { label: "Program length", render: (u) => u.program_length || "—" },
    {
      label: "Fit for you",
      render: (u) => u.fit_notes || "—",
    },
    {
      label: "Highlights",
      render: (u) => {
        const h = Array.isArray(u.highlights) ? u.highlights : [];
        return h.length ? (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {h.slice(0, 4).map((x, i) => <li key={`h-${i}`} style={{ marginBottom: 2 }}>{x}</li>)}
          </ul>
        ) : "—";
      },
    },
    {
      label: "Gaps",
      render: (u) => {
        const g = Array.isArray(u.gaps) ? u.gaps : [];
        return g.length ? (
          <ul style={{ margin: 0, paddingLeft: 16, color: "var(--warn)" }}>
            {g.slice(0, 4).map((x, i) => <li key={`g-${i}`} style={{ marginBottom: 2 }}>{x}</li>)}
          </ul>
        ) : "—";
      },
    },
    {
      label: "Checklist",
      render: (u) => {
        const cl = Array.isArray(u.checklist) ? u.checklist : [];
        const done = cl.filter((c) => c.done).length;
        return cl.length ? `${done}/${cl.length} complete` : "—";
      },
    },
  ];

  return (
    <div className="compare-sheet" data-testid="compare-sheet">
      <div className="compare-sheet-head">
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)" }}>
            Side-by-side comparison
          </div>
          <h3 style={{ margin: "4px 0 0 0", fontSize: 18 }}>Compare universities</h3>
        </div>
        <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="compare-close">
          Close
        </button>
      </div>

      <div className="compare-picker">
        <span style={{ fontSize: 12, color: "var(--ink-3)", alignSelf: "center", marginRight: 4 }}>
          Pick up to 4 universities:
        </span>
        {universities.map((u) => {
          const on = compareIds.includes(u.id);
          const disabled = !on && compareIds.length >= 4;
          return (
            <button
              key={u.id}
              className={`compare-chip ${on ? "on" : ""}`}
              onClick={() => onToggle(u.id)}
              disabled={disabled}
              data-testid={`compare-chip-${u.id}`}
              title={disabled ? "Compare a max of 4 universities at once" : ""}
              style={disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, background: "var(--ink)" }}></span>
              {u.short || u.name}
            </button>
          );
        })}
      </div>

      {selected.length === 0 ? (
        <div style={{ padding: 28, textAlign: "center", color: "var(--ink-3)", fontSize: 13 }}>
          Pick at least 2 universities above to see them side-by-side.
        </div>
      ) : (
        <div className="compare-grid">
          <table className="compare-table" data-testid="compare-table">
            <thead>
              <tr>
                <th></th>
                {selected.map((u) => (
                  <th key={u.id}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: "var(--ink)" }}></span>
                      <span>{u.name}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  {selected.map((u) => (
                    <td key={u.id}>{r.render(u)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
